/**
 * AdminComposedDocumentBody — 슈퍼관리자 보고서 관리에서 COMPOSED 본문을 read-only로 렌더.
 *
 * apps/web ComposedDocumentBody의 admin 축약본. 서버 상세 응답이 templateSnapshot·content·liveData
 * (composedLiveData)를 모두 동봉하므로 내부 live fetch는 없다(스냅샷이 정본). 스냅샷이 없으면
 * templateKey로 admin 템플릿을 조회해 폴백한다.
 *
 * 렌더 코어는 apps/web과 동일: resolveForRender(template, content, _, liveData) → rc 정본
 * PagedComposerView(낱장 측정·분할·렌더). admin 복제본은 두지 않는다 — 조용히 갈라지기 때문.
 * 결재란(approvalBlock)은 공유 ApprovalBlockSpecial로 그리며, 표시 입력은 호출자가 빌드한
 * approvalData(ApprovalBlockData)를 RenderData의 `approval` 키로 주입해 공급한다 — 주입이 없으면
 * special이 빈 결재란을 그리므로(결재 완료본이 공란) 상세 화면은 반드시 넘겨야 한다.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ApprovalBlockSpecial,
  PagedComposerView,
  resolveForRender,
} from '@safetyhub/report-composer';
import type {
  ApprovalBlockData,
  ReportTemplate,
  RenderData,
  SpecialComponentMap,
} from '@safetyhub/report-composer';
import { adminApi } from '../../lib/api';

const COMPOSER_SPECIAL_COMPONENTS: SpecialComponentMap = {
  approvalBlock: ApprovalBlockSpecial,
};

const SNAPSHOT_CORRUPT_MESSAGE = '양식 스냅샷이 손상되었습니다.';
const TEMPLATE_CORRUPT_MESSAGE = '양식 데이터가 손상되었습니다.';

/** 렌더가 무조건 읽는 page 형상(pagePx의 size + 낱장 높이 산출의 margins) 검사. */
function isRenderablePage(page: unknown): boolean {
  if (typeof page !== 'object' || page === null) return false;
  const { size, margins } = page as { size?: unknown; margins?: unknown };
  if (typeof size !== 'string') return false;
  if (typeof margins !== 'object' || margins === null) return false;
  const m = margins as Record<string, unknown>;
  return (['top', 'right', 'bottom', 'left'] as const).every(
    (k) => typeof m[k] === 'number' && Number.isFinite(m[k]),
  );
}

/**
 * templateSnapshot(계약상 unknown — 생성 시 박제된 JSON)이 렌더 가능한 형상인지 검사한다.
 * 무검증 캐스트로 넘기면 손상된 스냅샷에서 page/body 접근이 throw해 상세 모달 전체가
 * ErrorBoundary 폴백으로 날아가므로, 여기서 걸러 본문 영역만 안내로 대체한다.
 */
function isRenderableTemplate(value: unknown): value is ReportTemplate {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const t = value as { page?: unknown; body?: unknown };
  return Array.isArray(t.body) && isRenderablePage(t.page);
}

/** null=템플릿 미확정(로딩) / ok:false=본문 resolve 실패(손상) / ok:true=렌더 입력 확보. */
type RenderResult = { ok: true; data: RenderData } | { ok: false } | null;

export interface AdminComposedDocumentBodyProps {
  /** 생성 시 박제한 양식 스냅샷(StoredReportTemplate). 우선 사용(무결성). */
  templateSnapshot?: unknown;
  /** 스냅샷이 없을 때 조회할 양식 식별자. */
  templateKey?: string | null;
  /** 보고서 본문 데이터(select-경로 평탄 객체). 계약상 unknown. */
  content: unknown;
  /** 서버가 resolve해 동봉한 live 바인딩(서명 등) 현재값. */
  liveData?: Record<string, unknown> | null;
  /** 결재란(approvalBlock special)이 읽는 결재 데이터. 미전달 시 결재란은 빈 상태. */
  approvalData?: ApprovalBlockData;
}

export function AdminComposedDocumentBody({
  templateSnapshot,
  templateKey,
  content,
  liveData,
  approvalData,
}: AdminComposedDocumentBodyProps) {
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    setTemplateError(null);
    if (templateSnapshot) {
      // 박제 스냅샷은 계약상 unknown — 형상 검증 없이 캐스트하면 손상본에서 렌더가 throw한다.
      if (!isRenderableTemplate(templateSnapshot)) {
        setTemplate(null);
        setTemplateError(SNAPSHOT_CORRUPT_MESSAGE);
        return;
      }
      setTemplate(templateSnapshot);
      return;
    }
    if (!templateKey) {
      setTemplate(null);
      return;
    }
    let cancelled = false;
    setTemplate(null);
    adminApi
      .getReportTemplate(templateKey)
      .then((res) => {
        if (cancelled) return;
        const fetched: unknown = res.data;
        if (!isRenderableTemplate(fetched)) {
          setTemplateError(TEMPLATE_CORRUPT_MESSAGE);
          return;
        }
        setTemplate(fetched);
      })
      .catch(() => {
        if (!cancelled) setTemplateError('양식을 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, [templateKey, templateSnapshot]);

  // resolve 실패(형상은 통과했으나 노드가 손상된 경우)도 본문 영역 안내로 흡수한다.
  const renderResult = useMemo<RenderResult>(() => {
    if (!template) return null;
    const raw = content as unknown;
    const isPlainObject =
      typeof raw === 'object' && raw !== null && !Array.isArray(raw);
    const contentObj = (isPlainObject ? raw : {}) as Record<string, unknown>;
    try {
      // approval(결재란 special 입력)은 resolve 산출 위에 키로 주입 — web ComposedDocumentBody와 동일.
      return {
        ok: true,
        data: {
          ...resolveForRender(template, contentObj, undefined, liveData ?? undefined),
          ...(approvalData ? { approval: approvalData } : {}),
        },
      };
    } catch (err) {
      console.warn('[AdminComposedDocumentBody] 본문 resolve 실패', err);
      return { ok: false };
    }
  }, [template, content, liveData, approvalData]);

  if (templateError) {
    return (
      <div className="rounded-card bg-red-50 p-4">
        <p className="text-sm text-red-700">{templateError}</p>
      </div>
    );
  }

  if (renderResult && !renderResult.ok) {
    return (
      <div className="rounded-card bg-red-50 p-4">
        <p className="text-sm text-red-700">{SNAPSHOT_CORRUPT_MESSAGE}</p>
      </div>
    );
  }

  if (!template || !renderResult) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <PagedComposerView
        template={template}
        data={renderResult.data}
        specialComponents={COMPOSER_SPECIAL_COMPONENTS}
      />
    </div>
  );
}

export default AdminComposedDocumentBody;
